import { gql, useQuery } from '@apollo/client';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { useParams } from 'react-router-dom';

import AccountPaneHeader from '../../components/headers/AccountPaneHeader';
import { isCreditCardAccount, isDepositoryAccount } from '../../utils/accounts';

import CreditAccountPane from './CreditAccountPane';
import DepositoryAccountPane from './DepositoryAccountPane';

import type { GetFinancialAccountQuery, GetFinancialAccountQueryVariables } from './__generated__/AccountPane.gql';

const GET_FINANCIAL_ACCOUNT = gql`
  query GetFinancialAccount($id: ID!) {
    getFinancialAccount(id: $id) {
      id
      type
      currentBalance {
        id
        balance
        creditLimit
        timestamp
      }
      statementBill {
        id
        amountDue
        minAmountDue
        dueDate
        statementDate
        currencyCode
      }
      ...AccountPaneHeader
    }
  }
  ${AccountPaneHeader.Fragment}
`;

interface IAccountPaneProps {
  // Stories pass this directly so they don't need a router with a matching route
  // pattern. Production rendering from MainApp.tsx omits the prop and the
  // useParams() fallback kicks in.
  accountId?: string;
}

const AccountPane = ({ accountId: accountIdProp }: IAccountPaneProps = {}) => {
  const params = useParams();
  const accountId = accountIdProp ?? params.accountId ?? '';

  const { data, loading, error } = useQuery<GetFinancialAccountQuery, GetFinancialAccountQueryVariables>(
    GET_FINANCIAL_ACCOUNT,
    {
      variables: { id: accountId },
    }
  );

  if (loading) {
    return <LoadingIndicator className="h-full" size="lg" fullScreen />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const financialAccount = data?.getFinancialAccount;

  if (!financialAccount) {
    return <div>Financial account not found</div>;
  }

  // Route to the appropriate component based on account type
  if (isCreditCardAccount(financialAccount)) {
    return <CreditAccountPane financialAccount={financialAccount} />;
  }

  if (isDepositoryAccount(financialAccount)) {
    return <DepositoryAccountPane financialAccount={financialAccount} />;
  }

  return (
    <div className="flex h-full w-full flex-col gap-2 pt-6">
      <div className="flex h-full w-full flex-col gap-8 overflow-y-auto p-4">
        <div className="text-primary text-2xl">Account: {financialAccount.name}</div>
        <div className="text-secondary">
          Account type {financialAccount.type} - {financialAccount.subtype} is not yet supported
        </div>
      </div>
    </div>
  );
};

export default AccountPane;
